import { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import {
  Save, ArrowLeft, FileText, Plus, Trash2, ExternalLink,
  Upload, ChevronRight, AlertTriangle, CheckCircle, StickyNote,
  Mail, Send, Calendar as CalendarIcon, History, FileImage, Eye, Download, X, Wrench
} from 'lucide-react';
import { cn } from '../lib/utils';

const PHASE_LABELS = { 1: 'Erfassung', 2: 'Terminierung', 3: 'Rapportieren', 4: 'Verrechnen', 5: 'Zahlungseingang' };
const PHASE_COLORS = { 1: 'phase-1', 2: 'phase-2', 3: 'phase-3', 4: 'phase-4', 5: 'phase-5' };

const emptyLine = () => ({ description: '', details: '', quantity: 1, unit: 'Stk.', unit_price: 0, discount_pct: 0, skonto_pct: 0 });

function FilePreviewModal({ doc, onClose }) {
  const isImage = doc?.content_type?.startsWith('image/');
  const isPdf = doc?.content_type === 'application/pdf';
  const dataUrl = doc ? `data:${doc.content_type};base64,${doc.data}` : '';

  const handleDownload = () => {
    const a = document.createElement('a');
    a.href = dataUrl;
    a.download = doc.filename;
    a.click();
  };

  return (
    <Dialog open={!!doc} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2 pr-8">
            <FileText className="w-4 h-4 text-primary" strokeWidth={1.5} />
            {doc?.filename}
          </DialogTitle>
        </DialogHeader>
        <div className="flex-1 overflow-auto min-h-0">
          {isImage && (
            <img src={dataUrl} alt={doc.filename} className="max-w-full mx-auto rounded" />
          )}
          {isPdf && (
            <iframe src={dataUrl} title={doc.filename} className="w-full h-[65vh] rounded border" />
          )}
          {!isImage && !isPdf && (
            <div className="flex flex-col items-center justify-center py-12 gap-3">
              <FileText className="w-12 h-12 text-muted-foreground" strokeWidth={1} />
              <p className="text-sm text-muted-foreground">Vorschau nicht verfügbar</p>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-sm">Schliessen</Button>
          <Button onClick={handleDownload} className="rounded-sm gap-2">
            <Download className="w-4 h-4" />Herunterladen
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function OrderDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isNew = !id;

  const [order, setOrder] = useState(null);
  const [form, setForm] = useState({
    title: '', customer_id: '', customer_name: '',
    customer_address: '', customer_email: '', customer_phone: '',
    owner_id: '', owner_name: '', owner_address: '', owner_email: '', owner_phone: '', owner_notes: '',
    property_id: '', property_address: '',
    renter_id: '', renter_name: '', renter_first_name: '', renter_last_name: '', renter_email: '', renter_phone: '',
    object_name: '', cost_limit: '', notes: '', private_notes: '', deadline: '', received_at: '',
    monteur_company: '', monteur_name: '', monteur_address: '', monteur_email: '', monteur_phone: '',
    invoice_due_date: '', invoice_lines: []
  });
  const [customers, setCustomers] = useState([]);
  const [owners, setOwners] = useState([]);
  const [contractors, setContractors] = useState([]);
  const [properties, setProperties] = useState([]);
  const [renters, setRenters] = useState([]);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [phaseDlg, setPhaseDlg] = useState(false);
  const [paymentDlg, setPaymentDlg] = useState(false);
  const [phaseNote, setPhaseNote] = useState('');
  const [cancelDlg, setCancelDlg] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [emailDlg, setEmailDlg] = useState(false);
  const [emailForm, setEmailForm] = useState({ to_email: '', subject: '', message: '' });
  const [sendingEmail, setSendingEmail] = useState(false);
  const [contractorEmailDlg, setContractorEmailDlg] = useState(false);
  const [contractorEmailForm, setContractorEmailForm] = useState({ to_email: '', subject: '', message: '' });
  const [sendingContractorEmail, setSendingContractorEmail] = useState(false);
  const [reminderDlg, setReminderDlg] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadingContractor, setUploadingContractor] = useState(false);
  const [history, setHistory] = useState([]);
  const [previewDoc, setPreviewDoc] = useState(null);
  const [showNewCustomerDetails, setShowNewCustomerDetails] = useState(false);
  const [showNewOwnerDetails, setShowNewOwnerDetails] = useState(false);
  const [showNewPropertyDetails, setShowNewPropertyDetails] = useState(false);
  const [showNewRenterDetails, setShowNewRenterDetails] = useState(false);
  const [customerSearch, setCustomerSearch] = useState('');
  const [ownerSearch, setOwnerSearch] = useState('');
  const [contractorSearch, setContractorSearch] = useState('');
  const [propertySearch, setPropertySearch] = useState('');
  const [renterSearch, setRenterSearch] = useState('');
  const { isAdmin } = useAuth();

  useEffect(() => {
    Promise.all([
      api.get('/customers'),
      api.get('/owners'),
      api.get('/contractors'),
      api.get('/properties'),
      api.get('/renters')
    ]).then(([c, o, ct, p, r]) => {
      setCustomers(c.data);
      setOwners(o.data);
      setContractors(ct.data);
      setProperties(p.data);
      setRenters(r.data);
    });

    if (!isNew) {
      api.get(`/orders/${id}`)
        .then(res => {
          setOrder(res.data);
          setForm({
            ...res.data,
            invoice_lines: res.data.invoice_lines || [],
            cost_limit: res.data.cost_limit || '',
            received_at: res.data.received_at || '',
          });
        })
        .catch(() => { toast.error('Auftrag nicht gefunden'); navigate('/orders'); })
        .finally(() => setLoading(false));
      api.get(`/orders/${id}/history`)
        .then(res => setHistory(res.data.history || []))
        .catch(() => {});
    }
  }, [id, isNew, navigate]);

  const computeTotals = useCallback((lines) => {
    let subtotal = 0;
    lines.forEach(line => {
      const lt = (line.quantity || 0) * (line.unit_price || 0);
      const afterDisc = lt * (1 - (line.discount_pct || 0) / 100);
      const afterSkonto = afterDisc * (1 - (line.skonto_pct || 0) / 100);
      subtotal += afterSkonto;
    });
    return subtotal;
  }, []);

  const handleSendEmail = async (isReminder = false) => {
    if (!emailForm.to_email) { toast.error('E-Mail-Adresse eingeben'); return; }
    setSendingEmail(true);
    try {
      const endpoint = isReminder ? `/reminders/send/${id}` : `/orders/${id}/send-email`;
      await api.post(endpoint, emailForm);
      toast.success('E-Mail erfolgreich gesendet');
      setEmailDlg(false); setReminderDlg(false);
      setEmailForm({ to_email: '', subject: '', message: '' });
      const res = await api.get(`/orders/${id}`);
      setOrder(res.data);
    } catch (err) { toast.error(err.response?.data?.detail || 'E-Mail konnte nicht gesendet werden'); }
    finally { setSendingEmail(false); }
  };

  const openContractorEmailDialog = () => {
    setContractorEmailForm({
      to_email: form.monteur_email || order?.monteur_email || '',
      subject: `Auftrag ${order?.order_number || ''} – ${order?.title || form.title || ''}`.trim(),
      message: '',
    });
    setContractorEmailDlg(true);
  };

  const handleSendContractorEmail = async () => {
    if (!contractorEmailForm.to_email) { toast.error('Beim Dienstleister ist keine E-Mail-Adresse hinterlegt'); return; }
    setSendingContractorEmail(true);
    try {
      await api.post(`/orders/${id}/send-contractor-email`, contractorEmailForm);
      toast.success('Auftrag an Dienstleister gesendet');
      setContractorEmailDlg(false);
      const res = await api.get(`/orders/${id}`);
      setOrder(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'E-Mail konnte nicht gesendet werden');
    } finally {
      setSendingContractorEmail(false);
    }
  };

  const handleCalendarEvent = async () => {
    try {
      const res = await api.post(`/orders/${id}/calendar-event`);
      if (!res.data.configured) toast.info('Microsoft Graph nicht konfiguriert. Admin → Einstellungen → Microsoft Graph.');
      else toast.success(res.data.message);
    } catch { toast.error('Fehler'); }
  };

  const handleDelete = async () => {
    if (!window.confirm('Auftrag wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden.')) return;
    try {
      await api.delete(`/orders/${id}`);
      toast.success('Auftrag gelöscht');
      navigate('/orders');
    } catch (err) { toast.error(err.response?.data?.detail || 'Fehler beim Löschen'); }
  };

  const handleCancel = async () => {
    try {
      await api.post(`/orders/${id}/cancel`, { reason: cancelReason });
      toast.success('Auftrag abgebrochen und archiviert');
      navigate('/orders');
    } catch (err) { toast.error(err.response?.data?.detail || 'Fehler'); }
  };

  const setField = (key, val) => setForm(f => ({ ...f, [key]: val }));
  const setLine = (idx, key, val) => setForm(f => ({
    ...f,
    invoice_lines: f.invoice_lines.map((l, i) => i === idx ? { ...l, [key]: val } : l)
  }));
  const addLine = () => setForm(f => ({ ...f, invoice_lines: [...f.invoice_lines, emptyLine()] }));
  const removeLine = (idx) => setForm(f => ({ ...f, invoice_lines: f.invoice_lines.filter((_, i) => i !== idx) }));

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        ...form,
        cost_limit: form.cost_limit ? parseFloat(form.cost_limit) : null,
        invoice_lines: form.invoice_lines.map(l => ({
          ...l,
          quantity: parseFloat(l.quantity) || 1,
          unit_price: parseFloat(l.unit_price) || 0,
          discount_pct: parseFloat(l.discount_pct) || 0,
          skonto_pct: parseFloat(l.skonto_pct) || 0,
        }))
      };
      if (isNew) {
        const res = await api.post('/orders', payload);
        toast.success('Auftrag erstellt');
        navigate(`/orders/${res.data.id}`);
      } else {
        await api.put(`/orders/${id}`, payload);
        toast.success('Gespeichert');
        const res = await api.get(`/orders/${id}`);
        setOrder(res.data);
        setForm(f => ({
          ...res.data,
          invoice_lines: res.data.invoice_lines || [],
          cost_limit: res.data.cost_limit || '',
          received_at: res.data.received_at || '',
        }));
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Speichern fehlgeschlagen');
    } finally {
      setSaving(false);
    }
  };

  const handlePhaseChange = async (newPhase) => {
    if (newPhase === 5) { setPaymentDlg(true); setPhaseDlg(false); return; }
    try {
      await api.patch(`/orders/${id}/phase`, { phase: newPhase, internal_note: phaseNote || undefined });
      toast.success(`Phase ${newPhase}: ${PHASE_LABELS[newPhase]}`);
      const res = await api.get(`/orders/${id}`);
      setOrder(res.data);
      setPhaseDlg(false);
      setPhaseNote('');
    } catch (err) {
      toast.error('Fehler beim Phasenwechsel');
    }
  };

  const handlePaymentConfirm = async () => {
    try {
      await api.patch(`/orders/${id}/phase`, { phase: 5, internal_note: 'Zahlung eingegangen – Archiviert' });
      toast.success('Zahlung bestätigt – Auftrag archiviert');
      navigate('/orders');
    } catch (err) {
      toast.error('Fehler');
    }
  };

  const uploadFiles = async (files, contractor = false) => {
    const fileList = Array.from(files || []);
    if (!fileList.length) return;
    const setBusy = contractor ? setUploadingContractor : setUploading;
    setBusy(true);
    try {
      for (const file of fileList) {
        const fd = new FormData();
        fd.append('file', file);
        await api.post(`/orders/${id}/upload${contractor ? '?contractor=true' : ''}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      }
      toast.success(fileList.length === 1 ? 'Datei hochgeladen' : 'Dateien hochgeladen');
      const res = await api.get(`/orders/${id}`);
      setOrder(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload fehlgeschlagen');
    } finally {
      setBusy(false);
    }
  };
  const handleUpload = async (e) => {
    await uploadFiles(e.target.files);
    e.target.value = '';
  };

  const openPdf = async (includeQr = true) => {
    try {
      const response = await api.get(`/orders/${id}/pdf?qr=${includeQr}`, { responseType: 'blob' });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank');
    } catch (err) {
      toast.error('PDF konnte nicht geladen werden');
    }
  };

  const getFileIcon = (contentType) => {
    if (!contentType) return <FileText className="w-3.5 h-3.5 shrink-0" strokeWidth={1.5} />;
    if (contentType.startsWith('image/')) return <FileImage className="w-3.5 h-3.5 shrink-0 text-blue-500" strokeWidth={1.5} />;
    if (contentType === 'application/pdf') return <FileText className="w-3.5 h-3.5 shrink-0 text-red-500" strokeWidth={1.5} />;
    return <FileText className="w-3.5 h-3.5 shrink-0" strokeWidth={1.5} />;
  };

  const subtotal = computeTotals(form.invoice_lines || []);
  const mwstFactor = order?.totals?.subtotal ? ((order.totals.mwst_amount || 0) / order.totals.subtotal) : 0;
  const liveMwst = subtotal * mwstFactor;
  const liveTotal = subtotal + liveMwst;
  const matchSearch = (value, term) => (value || '').toString().toLowerCase().includes(term.trim().toLowerCase());
  const filteredCustomers = customers.filter(c => !customerSearch.trim() || [c.name, c.address, c.email, c.phone].some(v => matchSearch(v, customerSearch)));
  const filteredOwners = owners.filter(o => !ownerSearch.trim() || [o.name, o.address, o.email, o.phone, o.notes].some(v => matchSearch(v, ownerSearch)));
  const filteredContractors = contractors.filter(c => !contractorSearch.trim() || [c.company, c.contact_name, c.address, c.email, c.phone, c.specialty, c.notes].some(v => matchSearch(v, contractorSearch)));
  const baseProperties = form.customer_id ? properties.filter(p => p.customer_id === form.customer_id) : properties;
  const filteredProperties = baseProperties.filter(p => !propertySearch.trim() || [p.address, p.object_number, p.description].some(v => matchSearch(v, propertySearch)));
  const baseRenters = form.property_id ? renters.filter(r => r.property_id === form.property_id) : renters;
  const filteredRenters = baseRenters.filter(r => !renterSearch.trim() || [`${r.first_name} ${r.last_name}`, r.email, r.phone, r.address].some(v => matchSearch(v, renterSearch)));

  if (loading) return (
    <Layout>
      <div className="flex items-center justify-center h-64">
        <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    </Layout>
  );

  return (
    <Layout>
      <div className="p-6 md:p-8 max-w-5xl" data-testid="order-detail-page">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <button onClick={() => navigate('/orders')} className="text-muted-foreground hover:text-foreground transition-colors">
            <ArrowLeft className="w-5 h-5" strokeWidth={1.5} />
          </button>
          <div className="flex-1 min-w-0">
            <h1 className="font-heading font-black text-2xl text-foreground truncate">
              {isNew ? 'Neuer Auftrag' : (order?.order_number || 'Auftrag')}
            </h1>
            {order && (
              <div className="flex items-center gap-2 mt-1">
                <span className={cn("text-xs font-medium px-2 py-0.5 rounded-sm", PHASE_COLORS[order.phase])}>
                  Phase {order.phase} – {PHASE_LABELS[order.phase]}
                </span>
              </div>
            )}
          </div>
          <div className="flex items-center gap-2 flex-wrap justify-end">
            {!isNew && (
              <>
                {/* PDF Split Button */}
                <div className="flex items-center border border-border rounded-sm overflow-hidden">
                  <Button variant="ghost" size="sm" onClick={() => openPdf(true)} data-testid="pdf-btn" className="rounded-none gap-2 border-r border-border">
                    <FileText className="w-4 h-4" strokeWidth={1.5} />
                    PDF + QR
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => openPdf(false)} className="rounded-none text-xs px-2" title="PDF ohne QR-Code">
                    ohne QR
                  </Button>
                </div>
                <Button variant="outline" size="sm" onClick={() => setEmailDlg(true)} data-testid="email-btn" className="rounded-sm gap-2">
                  <Mail className="w-4 h-4" strokeWidth={1.5} />
                  E-Mail
                </Button>
                <Button variant="outline" size="sm" onClick={openContractorEmailDialog} data-testid="contractor-email-btn" className="rounded-sm gap-2">
                  <Wrench className="w-4 h-4" strokeWidth={1.5} />
                  Dienstleister
                </Button>
                {order?.phase === 2 && (
                  <Button variant="outline" size="sm" onClick={handleCalendarEvent} data-testid="calendar-btn" className="rounded-sm gap-2">
                    <CalendarIcon className="w-4 h-4" strokeWidth={1.5} />
                    Kalender
                  </Button>
                )}
                {order?.phase === 4 && (
                  <Button variant="outline" size="sm" onClick={() => { setReminderDlg(true); setEmailForm(f => ({ ...f, subject: `Zahlungserinnerung: Auftrag ${order?.order_number}` })); }} data-testid="reminder-btn" className="rounded-sm gap-2 border-amber-200 text-amber-700 hover:bg-amber-50">
                    <Send className="w-4 h-4" strokeWidth={1.5} />
                    Mahnung
                  </Button>
                )}
                {order?.phase < 5 && (
                  <Button variant="outline" size="sm" onClick={() => setPhaseDlg(true)} data-testid="phase-change-btn" className="rounded-sm gap-2">
                    <ChevronRight className="w-4 h-4" />
                    Phase
                  </Button>
                )}
                {isAdmin && order?.phase < 5 && (
                  <Button variant="outline" size="sm" onClick={() => { setCancelReason(''); setCancelDlg(true); }} className="rounded-sm gap-2 border-orange-200 text-orange-700 hover:bg-orange-50">
                    <X className="w-4 h-4" />
                    Abbrechen
                  </Button>
                )}
                {isAdmin && (
                  <Button variant="outline" size="sm" onClick={handleDelete} className="rounded-sm gap-2 border-destructive/30 text-destructive hover:bg-destructive/10">
                    <Trash2 className="w-4 h-4" />
                    Löschen
                  </Button>
                )}
              </>
            )}
            <Button onClick={handleSave} disabled={saving} data-testid="save-order-btn" className="rounded-sm gap-2">
              <Save className="w-4 h-4" />
              {saving ? 'Speichern...' : 'Speichern'}
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Form */}
          <div className="lg:col-span-2 space-y-6">
            {/* Basic Info */}
            <div className="bg-card border border-border/60 rounded-sm p-6 space-y-4">
              <h3 className="font-heading font-semibold text-sm uppercase tracking-wider text-muted-foreground">Auftragsdaten</h3>
              <div className="space-y-3">
                <div className="space-y-1.5">
                  <Label>Titel</Label>
                  <Input data-testid="order-title-input" value={form.title} onChange={e => setField('title', e.target.value)} placeholder="Auftragstitel (optional)" className="rounded-sm" />
                </div>

                {/* Eingangsdatum + Deadline */}
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label>Eingangsdatum des Auftrags</Label>
                    <Input
                      data-testid="received-at-input"
                      type="date"
                      value={form.received_at || ''}
                      onChange={e => setField('received_at', e.target.value)}
                      className="rounded-sm"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label>Termin / Deadline</Label>
                    <Input data-testid="deadline-input" type="date" value={form.deadline || ''} onChange={e => setField('deadline', e.target.value)} className="rounded-sm" />
                  </div>
                </div>

                {/* Verwaltung */}
                <div className="space-y-2">
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1.5">
                      <Label>Verwaltung wählen</Label>
                      <Input value={customerSearch} onChange={e => setCustomerSearch(e.target.value)} placeholder="Verwaltung suchen..." className="rounded-sm h-8 text-sm" />
                      <Select value={form.customer_id} onValueChange={v => {
                        const cust = customers.find(c => c.id === v);
                        setForm(f => ({ ...f, customer_id: v, customer_name: cust?.name || '', customer_address: cust?.address || '', customer_email: cust?.email || '', customer_phone: cust?.phone || '' }));
                        setShowNewCustomerDetails(false);
                      }}>
                        <SelectTrigger className="rounded-sm" data-testid="customer-select"><SelectValue placeholder="Bestehend..." /></SelectTrigger>
                        <SelectContent>{filteredCustomers.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1.5">
                      <Label>oder Neue Verwaltung</Label>
                      <Input data-testid="customer-name-input" value={form.customer_name} onChange={e => { setField('customer_name', e.target.value); setField('customer_id', ''); setShowNewCustomerDetails(!!e.target.value); }} placeholder="Name eingeben..." className="rounded-sm" />
                    </div>
                  </div>
                  {(showNewCustomerDetails || (form.customer_id && (form.customer_address || form.customer_email || form.customer_phone))) && (
                    <div className="grid grid-cols-3 gap-2 bg-muted/50 rounded-sm p-3 border border-border/40">
                      <div className="space-y-1"><Label className="text-xs text-muted-foreground">Adresse</Label><Input value={form.customer_address || ''} onChange={e => setField('customer_address', e.target.value)} placeholder="Strasse, PLZ Ort" className="rounded-sm h-8 text-sm" /></div>
                      <div className="space-y-1"><Label className="text-xs text-muted-foreground">Telefon</Label><Input value={form.customer_phone || ''} onChange={e => setField('customer_phone', e.target.value)} placeholder="+41 79 ..." className="rounded-sm h-8 text-sm" /></div>
                      <div className="space-y-1"><Label className="text-xs text-muted-foreground">E-Mail</Label><Input value={form.customer_email || ''} onChange={e => setField('customer_email', e.target.value)} placeholder="name@mail.ch" className="rounded-sm h-8 text-sm" /></div>
                    </div>
                  )}
                </div>

                {/* Eigentümer */}
                <div className="space-y-2">
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1.5">
                      <Label>Eigentümer wählen</Label>
                      <Input value={ownerSearch} onChange={e => setOwnerSearch(e.target.value)} placeholder="Eigentümer suchen..." className="rounded-sm h-8 text-sm" />
                      <Select value={form.owner_id || ''} onValueChange={v => {
                        const owner = owners.find(o => o.id === v);
                        setForm(f => ({ ...f, owner_id: v, owner_name: owner?.name || '', owner_address: owner?.address || '', owner_email: owner?.email || '', owner_phone: owner?.phone || '', owner_notes: owner?.notes || '' }));
                        setShowNewOwnerDetails(false);
                      }}>
                        <SelectTrigger className="rounded-sm" data-testid="owner-select"><SelectValue placeholder="Bestehend..." /></SelectTrigger>
                        <SelectContent>{filteredOwners.map(o => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1.5">
                      <Label>oder Neuer Eigentümer</Label>
                      <Input value={form.owner_name || ''} onChange={e => { setField('owner_name', e.target.value); setField('owner_id', ''); setShowNewOwnerDetails(!!e.target.value); }} placeholder="Name eingeben..." className="rounded-sm" />
                    </div>
                  </div>
                  {(showNewOwnerDetails || (form.owner_id && (form.owner_address || form.owner_email || form.owner_phone || form.owner_notes))) && (
                    <div className="space-y-2 bg-muted/50 rounded-sm p-3 border border-border/40">
                      <div className="grid grid-cols-3 gap-2">
                        <div className="space-y-1"><Label className="text-xs text-muted-foreground">Adresse</Label><Input value={form.owner_address || ''} onChange={e => setField('owner_address', e.target.value)} placeholder="Strasse, PLZ Ort" className="rounded-sm h-8 text-sm" /></div>
                        <div className="space-y-1"><Label className="text-xs text-muted-foreground">Telefon</Label><Input value={form.owner_phone || ''} onChange={e => setField('owner_phone', e.target.value)} placeholder="+41 79 ..." className="rounded-sm h-8 text-sm" /></div>
                        <div className="space-y-1"><Label className="text-xs text-muted-foreground">E-Mail</Label><Input value={form.owner_email || ''} onChange={e => setField('owner_email', e.target.value)} placeholder="name@mail.ch" className="rounded-sm h-8 text-sm" /></div>
                      </div>
                      <div className="space-y-1"><Label className="text-xs text-muted-foreground">Notizen</Label><Textarea value={form.owner_notes || ''} onChange={e => setField('owner_notes', e.target.value)} rows={2} className="rounded-sm text-sm resize-y min-h-[56px]" /></div>
                    </div>
                  )}
                </div>

                {/* Liegenschaft */}
                <div className="space-y-2">
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1.5">
                      <Label>Liegenschaft wählen</Label>
                      <Input value={propertySearch} onChange={e => setPropertySearch(e.target.value)} placeholder="Liegenschaft suchen..." className="rounded-sm h-8 text-sm" />
                      <Select value={form.property_id} onValueChange={v => { setField('property_id', v); setField('property_address', filteredProperties.find(p => p.id === v)?.address || ''); setShowNewPropertyDetails(false); }}>
                        <SelectTrigger className="rounded-sm" data-testid="property-select"><SelectValue placeholder="Bestehend..." /></SelectTrigger>
                        <SelectContent>{filteredProperties.map(p => <SelectItem key={p.id} value={p.id}>{p.address}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1.5">
                      <Label>oder Neue Adresse</Label>
                      <Input data-testid="property-address-input" value={form.property_address} onChange={e => { setField('property_address', e.target.value); setField('property_id', ''); setShowNewPropertyDetails(!!e.target.value); }} placeholder="Adresse eingeben..." className="rounded-sm" />
                    </div>
                  </div>
                </div>

                {/* Mieter */}
                <div className="space-y-2">
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1.5">
                      <Label>Mieter wählen</Label>
                      <Input value={renterSearch} onChange={e => setRenterSearch(e.target.value)} placeholder="Mieter suchen..." className="rounded-sm h-8 text-sm" />
                      <Select value={form.renter_id} onValueChange={v => {
                        const r = filteredRenters.find(r => r.id === v);
                        setForm(f => ({ ...f, renter_id: v, renter_name: r ? `${r.first_name || ''} ${r.last_name || ''}`.trim() : '', renter_first_name: r?.first_name || '', renter_last_name: r?.last_name || '', renter_email: r?.email || '', renter_phone: r?.phone || '' }));
                        setShowNewRenterDetails(false);
                      }}>
                        <SelectTrigger className="rounded-sm"><SelectValue placeholder="Bestehend..." /></SelectTrigger>
                        <SelectContent>{filteredRenters.map(r => <SelectItem key={r.id} value={r.id}>{r.first_name} {r.last_name}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1.5">
                      <Label>oder Neuer Mieter</Label>
                      <Input value={form.renter_last_name || ''} onChange={e => { setField('renter_last_name', e.target.value); setField('renter_id', ''); setShowNewRenterDetails(!!e.target.value); }} placeholder="Nachname eingeben..." className="rounded-sm" />
                    </div>
                  </div>
                  {showNewRenterDetails && !form.renter_id && (
                    <div className="grid grid-cols-3 gap-2 bg-muted/50 rounded-sm p-3 border border-border/40">
                      <div className="space-y-1"><Label className="text-xs text-muted-foreground">Vorname</Label><Input value={form.renter_first_name || ''} onChange={e => setField('renter_first_name', e.target.value)} placeholder="Vorname" className="rounded-sm h-8 text-sm" /></div>
                      <div className="space-y-1"><Label className="text-xs text-muted-foreground">Telefon</Label><Input value={form.renter_phone || ''} onChange={e => setField('renter_phone', e.target.value)} placeholder="+41 79 ..." className="rounded-sm h-8 text-sm" /></div>
                      <div className="space-y-1"><Label className="text-xs text-muted-foreground">E-Mail</Label><Input value={form.renter_email || ''} onChange={e => setField('renter_email', e.target.value)} placeholder="name@mail.ch" className="rounded-sm h-8 text-sm" /></div>
                    </div>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label>Objekt / Einheit</Label>
                    <Input data-testid="object-name-input" value={form.object_name || ''} onChange={e => setField('object_name', e.target.value)} placeholder="z.B. Wohnung 3.OG links" className="rounded-sm" />
                  </div>
                  <div className="space-y-1.5">
                    <Label>Kostendach (CHF)</Label>
                    <Input data-testid="cost-limit-input" type="number" value={form.cost_limit} onChange={e => setField('cost_limit', e.target.value)} placeholder="0.00" className="rounded-sm" />
                  </div>
                </div>

                <div className="space-y-1.5">
                  <Label>Bemerkungen (Auf Dokument sichtbar)</Label>
                  <Textarea data-testid="notes-input" value={form.notes || ''} onChange={e => setField('notes', e.target.value)} placeholder="Bemerkungen für den Kunden..." rows={3} className="rounded-sm resize-none" />
                </div>
                <div className="space-y-1.5">
                  <Label>Interne Notizen (Nur für Sie sichtbar)</Label>
                  <Textarea data-testid="private-notes-input" value={form.private_notes || ''} onChange={e => setField('private_notes', e.target.value)} placeholder="Interne Notizen..." rows={3} className="rounded-sm resize-none border-amber-200 focus-visible:ring-amber-500 bg-amber-50/30" />
                </div>
              </div>
            </div>

            {/* Monteur / Dienstleister */}
            <div className="bg-card border border-border/60 rounded-sm p-6 space-y-4">
              <h3 className="font-heading font-semibold text-sm uppercase tracking-wider text-muted-foreground">Monteur / Dienstleister</h3>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label>Dienstleister auswählen</Label>
                  <Input value={contractorSearch} onChange={e => setContractorSearch(e.target.value)} placeholder="Dienstleister suchen..." className="rounded-sm h-8 text-sm" />
                  <Select value="" onValueChange={v => {
                    const contractor = contractors.find(c => c.id === v);
                    if (!contractor) return;
                    setForm(f => ({
                      ...f,
                      monteur_company: contractor.company || '',
                      monteur_name: contractor.contact_name || '',
                      monteur_address: contractor.address || '',
                      monteur_email: contractor.email || '',
                      monteur_phone: contractor.phone || '',
                    }));
                  }}>
                    <SelectTrigger className="rounded-sm" data-testid="contractor-select"><SelectValue placeholder="Bestehend..." /></SelectTrigger>
                    <SelectContent>{filteredContractors.map(c => <SelectItem key={c.id} value={c.id}>{[c.company, c.contact_name].filter(Boolean).join(' – ')}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label>Aktuelle Auswahl</Label>
                  <div className="min-h-[74px] rounded-sm border border-border/60 bg-muted/40 px-3 py-2 text-sm text-muted-foreground">
                    <p className="font-medium text-foreground">{form.monteur_company || 'Kein Dienstleister gewählt'}</p>
                    {form.monteur_name && <p>{form.monteur_name}</p>}
                    {form.monteur_email && <p>{form.monteur_email}</p>}
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label>Firma</Label>
                  <Input value={form.monteur_company || ''} onChange={e => setField('monteur_company', e.target.value)} placeholder="Firmenname" className="rounded-sm" />
                </div>
                <div className="space-y-1.5">
                  <Label>Name</Label>
                  <Input value={form.monteur_name || ''} onChange={e => setField('monteur_name', e.target.value)} placeholder="Ansprechperson" className="rounded-sm" />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label>Adresse</Label>
                <Input value={form.monteur_address || ''} onChange={e => setField('monteur_address', e.target.value)} placeholder="Strasse, PLZ Ort" className="rounded-sm" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label>E-Mail</Label>
                  <Input type="email" value={form.monteur_email || ''} onChange={e => setField('monteur_email', e.target.value)} placeholder="monteur@firma.ch" className="rounded-sm" />
                </div>
                <div className="space-y-1.5">
                  <Label>Telefon</Label>
                  <Input value={form.monteur_phone || ''} onChange={e => setField('monteur_phone', e.target.value)} placeholder="+41 79 000 00 00" className="rounded-sm" />
                </div>
              </div>
            </div>

            {/* Invoice Lines (Visible from Phase 3) */}
            {(!isNew && order?.phase >= 3) && (
              <div className="bg-card border border-border/60 rounded-sm p-6">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                  <div>
                    <h3 className="font-heading font-semibold text-sm uppercase tracking-wider text-muted-foreground">Rechnung</h3>
                    <p className="text-sm text-muted-foreground mt-1">
                      {form.invoice_lines?.length || 0} Positionen · Total CHF {liveTotal.toFixed(2)}
                    </p>
                  </div>
                  <Button onClick={() => navigate(`/orders/${id}/invoice`)} className="rounded-sm gap-2">
                    <ExternalLink className="w-4 h-4" />
                    Rechnung öffnen
                  </Button>
                </div>
              </div>
            )}
          </div>

          {/* Right Column */}
          <div className="space-y-4">
            {!isNew && (
              <div className="bg-card border border-border/60 rounded-sm p-5">
                <h3 className="font-heading font-semibold text-sm uppercase tracking-wider text-muted-foreground mb-3">Anhänge für Dienstleister</h3>
                <label className="block">
                  <input type="file" className="sr-only" multiple disabled={uploadingContractor} accept="image/*,.pdf,.doc,.docx,.txt" onChange={async e => { const input = e.target; await uploadFiles(input.files, true); input.value = ''; }} />
                  <div className="border-2 border-dashed border-border rounded-sm p-5 text-center cursor-pointer hover:border-primary/50" onDragOver={e => e.preventDefault()} onDrop={e => { e.preventDefault(); if (!uploadingContractor) uploadFiles(e.dataTransfer.files, true); }}>
                    <Upload className="w-6 h-6 text-primary mx-auto mb-2" />
                    <p className="text-sm font-medium">{uploadingContractor ? 'Wird hochgeladen...' : 'Bilder / Dateien hinzufügen'}</p>
                  </div>
                </label>
                <div className="mt-3 space-y-1.5">
                  {(order?.contractor_documents || []).map(doc => (
                    <div key={doc.id} className="flex items-center gap-2 text-xs p-2 bg-muted rounded-sm">
                      {getFileIcon(doc.content_type)}
                      <span className="truncate flex-1">{doc.filename}</span>
                      <button title="Anzeigen" aria-label="Datei anzeigen" onClick={() => setPreviewDoc(doc)}><Eye className="w-4 h-4" /></button>
                      <button title="Entfernen" aria-label="Datei entfernen" onClick={async () => {
                        if (!window.confirm('Datei aus Dienstleister-Anhängen entfernen?')) return;
                        try {
                          await api.delete(`/orders/${id}/contractor-documents/${doc.id}`);
                          const res = await api.get(`/orders/${id}`);
                          setOrder(res.data);
                        } catch { toast.error('Datei konnte nicht entfernt werden'); }
                      }}><Trash2 className="w-4 h-4 text-destructive" /></button>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {/* Documents */}
            {!isNew && (
              <div className="bg-card border border-border/60 rounded-sm p-5">
                <h3 className="font-heading font-semibold text-sm uppercase tracking-wider text-muted-foreground mb-3">Fotos & Dokumente</h3>
                <label className="block">
                  <input type="file" className="sr-only" onChange={handleUpload} accept="image/*,.pdf,.doc,.docx" multiple data-testid="file-upload-input" />
                  <div
                    className="border-2 border-dashed border-border rounded-sm p-5 text-center cursor-pointer hover:border-primary/50 hover:bg-primary/5 transition-all"
                    onDragOver={e => { e.preventDefault(); e.currentTarget.classList.add('border-primary'); }}
                    onDragLeave={e => e.currentTarget.classList.remove('border-primary')}
                    onDrop={e => { e.preventDefault(); e.currentTarget.classList.remove('border-primary'); uploadFiles(e.dataTransfer.files); }}
                  >
                    <Upload className="w-6 h-6 text-primary mx-auto mb-2" strokeWidth={1.5} />
                    <p className="text-sm font-medium text-foreground mb-1">
                      {uploading ? 'Wird hochgeladen...' : 'Foto / PDF / Dokument'}
                    </p>
                    <p className="text-xs text-muted-foreground">Bilder, PDF, Word-Dateien hier ablegen oder auswählen</p>
                  </div>
                </label>
                {order?.documents?.length > 0 && (
                  <div className="mt-3 space-y-1.5">
                    {order.documents.map(doc => (
                      <div
                        key={doc.id}
                        className="flex items-center gap-2 text-xs text-muted-foreground p-2 bg-muted rounded-sm hover:bg-muted/80 transition-colors group"
                      >
                        {getFileIcon(doc.content_type)}
                        <span className="truncate flex-1">{doc.filename}</span>
                        <button
                          onClick={() => setPreviewDoc(doc)}
                          className="opacity-0 group-hover:opacity-100 transition-opacity text-primary hover:text-primary/80"
                          title="Anzeigen"
                        >
                          <Eye className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Internal Notes */}
            {!isNew && order?.internal_notes?.length > 0 && (
              <div className="bg-card border border-border/60 rounded-sm p-5">
                <h3 className="font-heading font-semibold text-sm uppercase tracking-wider text-muted-foreground mb-3 flex items-center gap-2">
                  <StickyNote className="w-4 h-4" strokeWidth={1.5} />
                  Interne Notizen
                </h3>
                <div className="space-y-2">
                  {order.internal_notes.map((note, idx) => (
                    <div key={idx} className="text-xs p-2 bg-muted rounded-sm">
                      <p className="font-medium text-foreground">{note.text}</p>
                      <p className="text-muted-foreground mt-0.5">{note.user} · {new Date(note.created_at).toLocaleDateString('de-CH')}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Rechnung Status */}
            {!isNew && order?.phase >= 4 && (
              <div className="bg-card border border-border/60 rounded-sm p-5 space-y-3">
                <h3 className="font-heading font-semibold text-sm uppercase tracking-wider text-muted-foreground flex items-center gap-2">
                  <Send className="w-4 h-4" strokeWidth={1.5} />
                  Rechnung
                </h3>
                {order.invoice_sent_at ? (
                  <div className="space-y-2">
                    <div className="flex items-center gap-2 text-sm">
                      <CheckCircle className="w-4 h-4 text-emerald-600" strokeWidth={1.5} />
                      <span className="text-emerald-700 font-medium">Gesendet am</span>
                      <span className="font-mono text-xs">{new Date(order.invoice_sent_at).toLocaleDateString('de-CH')}</span>
                    </div>
                    {order.invoice_due_date && (() => {
                      const today = new Date().toISOString().split('T')[0];
                      const isOverdue = order.invoice_due_date < today;
                      return (
                        <div className={cn("flex items-center gap-2 text-sm", isOverdue ? "text-red-600" : "text-muted-foreground")}>
                          <AlertTriangle className="w-4 h-4" strokeWidth={1.5} />
                          <span className="font-medium">Zahlbar bis</span>
                          <span className="font-mono text-xs">{new Date(order.invoice_due_date + 'T00:00:00').toLocaleDateString('de-CH')}</span>
                          {isOverdue && <span className="text-xs font-bold">– ÜBERFÄLLIG</span>}
                        </div>
                      );
                    })()}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground">Noch nicht gesendet</p>
                )}
                <div className="space-y-1.5 pt-1">
                  <Label className="text-xs">Zahlungsfrist manuell setzen</Label>
                  <Input type="date" value={form.invoice_due_date || ''} onChange={e => setField('invoice_due_date', e.target.value)} className="rounded-sm h-8 text-sm" />
                </div>
              </div>
            )}

            {/* History */}
            {!isNew && history.length > 0 && (
              <div className="bg-card border border-border/60 rounded-sm p-5">
                <h3 className="font-heading font-semibold text-sm uppercase tracking-wider text-muted-foreground mb-3 flex items-center gap-2">
                  <History className="w-4 h-4" strokeWidth={1.5} />
                  Verlauf
                </h3>
                <div className="space-y-2">
                  {history.map((h, idx) => (
                    <div key={idx} className="text-xs p-2 bg-muted rounded-sm border-l-2 border-primary/30">
                      <p className="font-medium text-foreground">{h.text}</p>
                      <p className="text-muted-foreground mt-0.5">{h.user} · {h.created_at ? new Date(h.created_at).toLocaleDateString('de-CH') : ''}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Datei-Vorschau Modal */}
      <FilePreviewModal doc={previewDoc} onClose={() => setPreviewDoc(null)} />

      {/* Email Dialog */}
      <Dialog open={emailDlg} onOpenChange={setEmailDlg}>
        <DialogContent data-testid="email-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <Mail className="w-5 h-5 text-primary" strokeWidth={1.5} />
              Auftrag per E-Mail senden
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="space-y-1.5">
              <Label>Empfänger E-Mail *</Label>
              <Input data-testid="email-to-input" type="email" value={emailForm.to_email} onChange={e => setEmailForm(f => ({ ...f, to_email: e.target.value }))} placeholder="empfaenger@beispiel.ch" className="rounded-sm" />
            </div>
            <div className="space-y-1.5">
              <Label>Betreff</Label>
              <Input value={emailForm.subject} onChange={e => setEmailForm(f => ({ ...f, subject: e.target.value }))} placeholder={`Auftrag ${order?.order_number}`} className="rounded-sm" />
            </div>
            <div className="space-y-1.5">
              <Label>Nachricht (optional)</Label>
              <Textarea value={emailForm.message} onChange={e => setEmailForm(f => ({ ...f, message: e.target.value }))} placeholder="Zusätzliche Nachricht..." rows={3} className="rounded-sm resize-none" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEmailDlg(false)} className="rounded-sm">Abbrechen</Button>
            <Button onClick={() => handleSendEmail(false)} disabled={sendingEmail} className="rounded-sm gap-2" data-testid="send-email-btn">
              <Send className="w-4 h-4" />{sendingEmail ? 'Senden...' : 'Senden'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Contractor Email Dialog */}
      <Dialog open={contractorEmailDlg} onOpenChange={setContractorEmailDlg}>
        <DialogContent data-testid="contractor-email-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <Wrench className="w-5 h-5 text-primary" strokeWidth={1.5} />
              Auftrag an Dienstleister senden
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <p className="text-sm text-muted-foreground">Auftrags-PDF und {order?.contractor_documents?.length || 0} Dienstleister-Anhänge</p>
            <div className="rounded-sm border border-border/60 bg-muted/40 p-3 text-sm">
              <p className="font-medium">{form.monteur_company || 'Dienstleister'}</p>
              {form.monteur_name && <p className="text-muted-foreground">{form.monteur_name}</p>}
            </div>
            <div className="space-y-1.5">
              <Label>Empfänger E-Mail *</Label>
              <Input type="email" value={contractorEmailForm.to_email} onChange={e => setContractorEmailForm(f => ({ ...f, to_email: e.target.value }))} placeholder="dienstleister@firma.ch" className="rounded-sm" />
            </div>
            <div className="space-y-1.5">
              <Label>Betreff</Label>
              <Input value={contractorEmailForm.subject} onChange={e => setContractorEmailForm(f => ({ ...f, subject: e.target.value }))} className="rounded-sm" />
            </div>
            <div className="space-y-1.5">
              <Label>Zusatznotiz (optional)</Label>
              <Textarea value={contractorEmailForm.message} onChange={e => setContractorEmailForm(f => ({ ...f, message: e.target.value }))} placeholder="Zusätzliche Information an den Dienstleister..." rows={3} className="rounded-sm resize-none" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setContractorEmailDlg(false)} className="rounded-sm">Abbrechen</Button>
            <Button onClick={handleSendContractorEmail} disabled={sendingContractorEmail} className="rounded-sm gap-2">
              <Send className="w-4 h-4" />{sendingContractorEmail ? 'Senden...' : 'Senden'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Payment Reminder Dialog */}
      <Dialog open={reminderDlg} onOpenChange={setReminderDlg}>
        <DialogContent data-testid="reminder-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <Send className="w-5 h-5 text-amber-600" strokeWidth={1.5} />
              Zahlungserinnerung senden
            </DialogTitle>
          </DialogHeader>
          <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-sm p-3 mb-3">
            <p className="text-sm text-amber-700 dark:text-amber-300">Auftrag {order?.order_number} – {order?.title}</p>
            {order?.totals?.total > 0 && <p className="text-xs font-mono font-bold text-amber-800 dark:text-amber-200 mt-0.5">Offener Betrag: CHF {order.totals.total.toFixed(2)}</p>}
          </div>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label>Empfänger E-Mail *</Label>
              <Input data-testid="reminder-email-input" type="email" value={emailForm.to_email} onChange={e => setEmailForm(f => ({ ...f, to_email: e.target.value }))} placeholder="empfaenger@beispiel.ch" className="rounded-sm" />
            </div>
            <div className="space-y-1.5">
              <Label>Zusätzliche Nachricht (optional)</Label>
              <Textarea value={emailForm.message} onChange={e => setEmailForm(f => ({ ...f, message: e.target.value }))} placeholder="z.B. Bitte begleichen Sie den Betrag bis..." rows={2} className="rounded-sm resize-none" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReminderDlg(false)} className="rounded-sm">Abbrechen</Button>
            <Button onClick={() => handleSendEmail(true)} disabled={sendingEmail} className="rounded-sm gap-2 bg-amber-600 hover:bg-amber-700" data-testid="send-reminder-btn">
              <Send className="w-4 h-4" />{sendingEmail ? 'Senden...' : 'Mahnung senden'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cancel Order Dialog */}
      <Dialog open={cancelDlg} onOpenChange={setCancelDlg}>
        <DialogContent data-testid="cancel-order-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <X className="w-5 h-5 text-orange-600" strokeWidth={1.5} />
              Auftrag abbrechen
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <p className="text-sm text-muted-foreground">
              Der Auftrag wird als abgebrochen archiviert. Die Begründung bleibt in der Übersicht sichtbar.
            </p>
            <div className="space-y-1.5">
              <Label>Begründung</Label>
              <Textarea
                value={cancelReason}
                onChange={e => setCancelReason(e.target.value)}
                placeholder="Warum wird der Auftrag abgebrochen?"
                rows={3}
                className="rounded-sm resize-none"
                data-testid="cancel-reason-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCancelDlg(false)} className="rounded-sm">Zurück</Button>
            <Button onClick={handleCancel} className="rounded-sm gap-2 bg-orange-600 hover:bg-orange-700" data-testid="confirm-cancel-order-btn">
              <X className="w-4 h-4" />
              Auftrag abbrechen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Phase Change Dialog */}
      <Dialog open={phaseDlg} onOpenChange={setPhaseDlg}>
        <DialogContent className="sm:max-w-md" data-testid="phase-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Phase wechseln</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <p className="text-sm text-muted-foreground">Aktuelle Phase: <strong>{PHASE_LABELS[order?.phase]}</strong></p>
            <div className="grid grid-cols-1 gap-2">
              {[1,2,3,4,5].map(p => (
                <button
                  key={p}
                  disabled={p === order?.phase}
                  onClick={() => handlePhaseChange(p)}
                  data-testid={`phase-btn-${p}`}
                  className={cn(
                    "flex items-center justify-between px-4 py-3 rounded-sm border text-sm font-medium transition-colors",
                    p === order?.phase
                      ? "opacity-40 cursor-not-allowed border-border bg-muted"
                      : "border-border hover:border-primary hover:bg-primary/5 cursor-pointer"
                  )}
                >
                  <span className={cn("text-xs font-medium px-2 py-0.5 rounded-sm", PHASE_COLORS[p])}>
                    Phase {p}
                  </span>
                  <span>{PHASE_LABELS[p]}</span>
                  {p === 5 && <CheckCircle className="w-4 h-4 text-emerald-500" strokeWidth={1.5} />}
                </button>
              ))}
            </div>
            <div className="space-y-1.5">
              <Label>Interne Notiz (optional)</Label>
              <Textarea value={phaseNote} onChange={e => setPhaseNote(e.target.value)} placeholder="Notiz zum Phasenwechsel..." rows={2} className="rounded-sm resize-none" />
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Payment Confirmation Dialog */}
      <Dialog open={paymentDlg} onOpenChange={setPaymentDlg}>
        <DialogContent data-testid="payment-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-emerald-500" strokeWidth={1.5} />
              Zahlungseingang bestätigen
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            Bestätigen Sie den Zahlungseingang für Auftrag <strong>{order?.order_number}</strong>.
            Der Auftrag wird danach archiviert.
          </p>
          {order?.totals?.total > 0 && (
            <div className="bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 rounded-sm p-4">
              <p className="text-sm font-medium text-emerald-700 dark:text-emerald-300">
                Betrag: CHF {order.totals.total.toFixed(2)}
              </p>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setPaymentDlg(false)} className="rounded-sm">Abbrechen</Button>
            <Button onClick={handlePaymentConfirm} className="rounded-sm gap-2 bg-emerald-600 hover:bg-emerald-700" data-testid="confirm-payment-btn">
              <CheckCircle className="w-4 h-4" />
              Zahlung bestätigen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
